import { FormEvent, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useStaffAuth } from '../auth/StaffAuthContext';
import { appPathFromReturnTo, errorMessage, isMtibEmail, normalizeEmail } from '../lib/utils';
import { useToast } from '../context/ToastContext';
import { storage } from '../lib/storage';

export default function StaffAuthPage() {
  const { identity, requestEmailOtp, verifyEmailOtp, refreshProfile, signOut } = useStaffAuth();
  const [params] = useSearchParams();
  const [pendingEmail, setPendingEmail] = useState(() => {
    const email = storage.getPendingStaffAuthEmail();
    return email && isMtibEmail(email) ? normalizeEmail(email) : '';
  });
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const actionInFlight = useRef(false);
  const navigate = useNavigate();
  const { showToast } = useToast();
  const returnTo = appPathFromReturnTo(params.get('returnTo'));

  useEffect(() => {
    if (identity.loading || identity.profileError || !identity.isAuthenticated) return;
    const destination = identity.hasProfile
      ? returnTo
      : `/staff-onboarding?returnTo=${encodeURIComponent(returnTo)}`;
    navigate(destination, { replace: true });
  }, [identity.hasProfile, identity.isAuthenticated, identity.loading, identity.profileError, navigate, returnTo]);

  useEffect(() => {
    if (identity.loading || identity.isAuthenticated || pendingEmail) return;
    storage.clearPendingStaffAuthEmail();
    navigate(`/?returnTo=${encodeURIComponent(returnTo)}`, { replace: true });
  }, [identity.isAuthenticated, identity.loading, navigate, pendingEmail, returnTo]);

  const resendCode = async () => {
    if (actionInFlight.current || !pendingEmail) return;
    actionInFlight.current = true;
    setBusy(true);
    try {
      await requestEmailOtp(pendingEmail);
      setCode('');
      showToast('Kod log masuk telah dihantar ke e-mel MTIB anda.', 'success');
    } catch (error) {
      showToast(errorMessage(error), 'error');
    } finally {
      actionInFlight.current = false;
      setBusy(false);
    }
  };

  const verifyCode = async (event: FormEvent) => {
    event.preventDefault();
    if (actionInFlight.current) return;
    if (!/^\d{6,10}$/.test(code)) {
      showToast('Masukkan kod pengesahan daripada e-mel anda.', 'error');
      return;
    }
    actionInFlight.current = true;
    setBusy(true);
    try {
      await verifyEmailOtp(pendingEmail, code);
      storage.clearPendingStaffAuthEmail();
      setPendingEmail('');
      showToast('Log masuk pegawai berjaya disahkan.', 'success');
    } catch (error) {
      showToast(errorMessage(error), 'error');
    } finally {
      actionInFlight.current = false;
      setBusy(false);
    }
  };

  const useDifferentEmail = () => {
    storage.clearPendingStaffAuthEmail();
    setPendingEmail('');
    setCode('');
    navigate(`/?returnTo=${encodeURIComponent(returnTo)}`, { replace: true });
  };

  if (identity.loading) {
    return <main className="auth-page"><div className="spinner" /></main>;
  }

  if (identity.isAuthenticated && identity.profileError) {
    return <main className="auth-page"><section className="auth-card">
      <img className="auth-logo-image" src="/mtib-logo.png" alt="Malaysian Timber Industry Board (MTIB)" />
      <div className="eyebrow">Log masuk pegawai MTIB</div>
      <h1>Profil tidak tersedia</h1>
      <p className="muted">Kod anda telah disahkan, tetapi profil pegawai tidak dapat dimuatkan. Ralat ini tidak dianggap sebagai profil yang tiada.</p>
      <p className="security-callout compact">{identity.profileError}</p>
      <div className="checkin-actions">
        <button type="button" className="button button-primary" disabled={busy} onClick={() => { setBusy(true); void refreshProfile().catch((error) => showToast(errorMessage(error), 'error')).finally(() => setBusy(false)); }}>Cuba muat semula profil</button>
        <button type="button" className="button button-secondary" disabled={busy} onClick={() => { setBusy(true); void signOut().catch((error) => showToast(errorMessage(error), 'error')).finally(() => setBusy(false)); }}>Log Keluar</button>
      </div>
    </section></main>;
  }

  return <main className="auth-page"><section className="auth-card">
    <img className="auth-logo-image" src="/mtib-logo.png" alt="Malaysian Timber Industry Board (MTIB)" />
    <div className="eyebrow">Log masuk pegawai MTIB</div>
    <h1>Masukkan kod anda</h1>
    <p className="muted">Kod dihantar ke: <strong>{pendingEmail}</strong></p>
    <form className="form-stack" onSubmit={verifyCode}>
      <label className="field"><span>Kod pengesahan</span><input autoFocus type="text" inputMode="numeric" pattern="[0-9]{6,10}" maxLength={10} value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 10))} placeholder="0000000000" autoComplete="one-time-code" required /></label>
      <button className="button button-primary button-block" disabled={busy || !/^\d{6,10}$/.test(code)}>{busy ? 'Sedang mengesahkan…' : 'Sahkan dan log masuk'}</button>
      <button type="button" className="button button-secondary button-block" disabled={busy} onClick={() => void resendCode()}>{busy ? 'Sila tunggu…' : 'Hantar semula kod'}</button>
      <button type="button" className="button button-secondary button-block" disabled={busy} onClick={useDifferentEmail}>Gunakan e-mel lain</button>
    </form>
    <p className="auth-footnote">Tetamu luar tidak memerlukan akaun pegawai. Kembali ke halaman <em>check-in</em> mesyuarat untuk meneruskan sebagai tetamu.</p>
  </section></main>;
}
